import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Play, Loader2, ArrowLeft, Gift } from "lucide-react";
import { fetchVenue, prizeEmoji, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CATEGORIES } from "@/data/mockData";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1559084323-63f8ed8557c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxwdWIlMjB0cml2aWElMjBuaWdodHxlbnwwfHx8fDE3ODUzNjk1NjZ8MA&ixlib=rb-4.1.0&q=85";

export default function VenueProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useApp();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchVenue(id)
      .then(setVenue)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const play = () => {
    if (!profile) {
      toast.message("Log in to play", { description: "Create your player profile first." });
      navigate("/login");
      return;
    }
    const cat = CATEGORIES.find((c) => c.id === venue.category)?.id || "general";
    navigate(`/setup?venue=${encodeURIComponent(venue.name)}&cat=${cat}&venueId=${venue.id}`);
  };

  if (loading) {
    return <div className="max-w-md mx-auto min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="animate-spin text-slate-600" /></div>;
  }
  if (notFound || !venue) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8 text-center">
        <p className="text-slate-400 mb-4">This venue profile doesn't exist.</p>
        <button onClick={() => navigate("/")} className="bg-amber-400 text-slate-950 font-bold rounded-xl px-6 py-3">Go Home</button>
      </div>
    );
  }

  const catName = CATEGORIES.find((c) => c.id === venue.category)?.name || "Trivia";

  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-950">
      <div className="relative h-56">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${venue.photo_url ? fileUrl(venue.photo_url) : BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30" />
        <button data-testid="venue-profile-back" onClick={() => navigate(-1)} className="absolute top-5 left-5 bg-slate-950/70 rounded-full p-2 text-slate-100 z-10">
          <ArrowLeft strokeWidth={2.5} size={20} />
        </button>
        <div className="absolute bottom-5 left-6 right-6 z-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{venue.type}</span>
          <h1 data-testid="venue-profile-name" className="font-display text-4xl font-black text-slate-50 leading-none mt-1">{venue.name}</h1>
          {venue.location && (
            <p className="text-slate-300 text-sm mt-2 flex items-center gap-1"><MapPin size={14} className="text-cyan-400" /> {venue.location} · {venue.distance} mi</p>
          )}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6">
        {venue.blurb && <p data-testid="venue-profile-blurb" className="text-slate-200 text-base leading-relaxed mb-6">{venue.blurb}</p>}

        {venue.prize?.title && (
          <div data-testid="venue-profile-prize" className="bg-slate-900 border border-amber-400/30 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={18} className="text-amber-400" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-amber-400">Prize on offer</span>
            </div>
            <p className="font-display font-black text-xl text-slate-50">{prizeEmoji(venue.prize.kind)} {venue.prize.title}</p>
            <p className="text-slate-400 text-sm mt-1">
              {venue.prize.condition_type === "leaderboard"
                ? "Win by topping the quiz leaderboard."
                : `Win by scoring ${venue.prize.min_score}/5 or higher.`}
            </p>
          </div>
        )}

        <div className="bg-slate-900 rounded-2xl p-4 mb-6 border border-slate-800">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Tonight's quiz</span>
          <p className="font-display font-bold text-lg text-cyan-400 mt-1">{catName}</p>
        </div>

        <motion.button
          data-testid="venue-play-btn"
          whileTap={{ scale: 0.97 }}
          onClick={play}
          className="w-full h-16 bg-amber-400 text-slate-950 rounded-2xl font-display font-black text-xl flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(245,158,11,0.4)] no-tap-highlight"
        >
          <Play strokeWidth={3} fill="currentColor" /> Play This Quiz
        </motion.button>
      </motion.div>
    </div>
  );
}
